import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Email as EmailIcon,
  AccountCircle as AccountIcon,
  Notifications as NotificationIcon,
  Security as SecurityIcon,
  Palette as TemplateIcon,
} from '@mui/icons-material';
import EmailConfigManager from './EmailConfigManager';
import AccountTemplateManager from './AccountTemplateManager';
import AccountSettings from './AccountSettings';
import { AlertInfo } from '../types';
import { getDefaultAccountId } from '../services/api';

interface SettingsProps {
  showAlert: (message: string, severity: AlertInfo['severity']) => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const Settings: React.FC<SettingsProps> = ({ showAlert }) => {
  const [currentTab, setCurrentTab] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Fixed Header */}
      <Box sx={{
        position: 'sticky',
        top: 0,
        zIndex: 10,
        backgroundColor: 'white',
        borderBottom: '1px solid',
        borderColor: 'divider',
        pb: 2,
        mb: 3
      }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
            Settings
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your account preferences and configurations
          </Typography>
        </Box>
      </Box>

      {/* Scrollable Content */}
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            aria-label="settings tabs"
            sx={{ px: 2 }}
          >
            <Tab
              icon={<EmailIcon />}
              label="Email Configuration"
              id="settings-tab-0"
              aria-controls="settings-tabpanel-0"
              sx={{ minHeight: 72 }}
            />
            <Tab
              icon={<TemplateIcon />}
              label="Templates"
              id="settings-tab-1"
              aria-controls="settings-tabpanel-1"
              sx={{ minHeight: 72 }}
            />
            <Tab
              icon={<AccountIcon />}
              label="Account"
              id="settings-tab-2"
              aria-controls="settings-tabpanel-2"
              sx={{ minHeight: 72 }}
            />
            <Tab
              icon={<NotificationIcon />}
              label="Notifications"
              id="settings-tab-3"
              aria-controls="settings-tabpanel-3"
              sx={{ minHeight: 72 }}
            />
            <Tab
              icon={<SecurityIcon />}
              label="Security"
              id="settings-tab-4"
              aria-controls="settings-tabpanel-4"
              sx={{ minHeight: 72 }}
            />
          </Tabs>
        </Box>

        <TabPanel value={currentTab} index={0}>
          <EmailConfigManager showAlert={showAlert} />
        </TabPanel>

        <TabPanel value={currentTab} index={1}>
          <Box sx={{ p: 3 }}>
            <AccountTemplateManager
              accountId={getDefaultAccountId()}
              showAlert={showAlert}
            />
          </Box>
        </TabPanel>

        <TabPanel value={currentTab} index={2}>
          <Box sx={{ p: 3 }}>
            <AccountSettings showAlert={showAlert} />
          </Box>
        </TabPanel>

        <TabPanel value={currentTab} index={3}>
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Notification Preferences
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Notification settings coming soon...
            </Typography>
          </Box>
        </TabPanel>

        <TabPanel value={currentTab} index={4}>
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Security Settings
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Security features coming soon...
            </Typography>
          </Box>
        </TabPanel>
        </Paper>
      </Box>
    </Box>
  );
};

export default Settings;
